/**
 * ============================================================================
 * RECURRING TRANSACTIONS v3.0 - TOKEN CONTEXT INTEGRATION
 * ============================================================================
 *
 * ✅ v3.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 *          - useToken() statt useAuth().getAccessToken()
 *          - Kein individueller Token-Fetch mehr
 *          - Verhindert Re-Render Loop & Token-Fehler
 * ✅ v2.0: PLANIVIO Rebranding + Token Fix
 * ✅ v1.0: Original Implementation
 *
 * Features:
 * - Monatliche Ausgaben (links) mit Toggle
 * - Monatliche Einnahmen (rechts) mit Toggle
 * - Summary (Einnahmen, Ausgaben, Differenz)
 * - Jährliche Zahlungen
 * - FAB Button zum Hinzufügen
 *
 * @version 3.0.0 - Token Context Integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v3.0: NEU!
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import Loading from '../common/Loading';
import budgetService from '../../services/budget.service';
import './RecurringTransactions.css';

console.log('🔄 RecurringTransactions.js v3.0 loaded - TOKEN CONTEXT INTEGRATION 🔐');

function RecurringTransactions({ active }) {
  // ✅ v3.0: useAuth NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated } = useAuth();

  // ✅ v3.0: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();

  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [recurring, setRecurring] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [toasts, setToasts] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'expense',
    category: '',
    frequency: 'monthly',
    day_of_month: 1,
    is_active: true
  });

  // Toast System
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // ============================================================================
  // ✅ v3.0: Load Data mit Token aus Context
  // ============================================================================
  const loadData = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    setLoading(true);
    try {
      console.log('🔑 RecurringTransactions: Using token from TokenContext');

      const [recurringData, categoriesData] = await Promise.all([
        budgetService.getRecurringTransactions(token).catch(() => ({ recurring: [] })),
        budgetService.getCategories(token).catch(() => ({ categories: [] }))
      ]);

      setRecurring(recurringData.recurring || []);
      setCategories(categoriesData.categories || []);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      showToast('Fehler beim Laden: ' + error.message, 'error');
    }
    setLoading(false);
  }, [isAuthenticated, token, showToast]);

  useEffect(() => {
    if (active && isAuthenticated && token && !tokenLoading) {
      loadData();
    }
  }, [active, isAuthenticated, token, tokenLoading, loadData]);

  // ✅ v3.0: Refresh mit Token aus Context
  const refreshData = async () => {
    if (!token) return;
    try {
      const data = await budgetService.getRecurringTransactions(token);
      setRecurring(data.recurring || []);
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    }
  };

  // ✅ v3.0: Toggle Active mit Token aus Context
  const handleToggle = async (item) => {
    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }
    try {
      await budgetService.toggleRecurringTransaction(token, item.id);
      await refreshData();
      showToast(item.is_active ? '⏸️ Deaktiviert' : '▶️ Aktiviert', 'success');
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // ✅ v3.0: Save mit Token aus Context
  const handleSave = async () => {
    if (!formData.description || !formData.amount) {
      showToast('Bitte alle Pflichtfelder ausfüllen', 'error');
      return;
    }

    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingItem) {
        await budgetService.updateRecurringTransaction(token, editingItem.id, data);
        showToast('✅ Dauerauftrag aktualisiert', 'success');
      } else {
        await budgetService.createRecurringTransaction(token, data);
        showToast('✅ Dauerauftrag erstellt', 'success');
      }

      setShowModal(false);
      setEditingItem(null);
      resetForm();
      await refreshData();
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // ✅ v3.0: Delete mit Token aus Context
  const handleDelete = async (id) => {
    if (!window.confirm('Dauerauftrag wirklich löschen?')) return;

    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }

    try {
      await budgetService.deleteRecurringTransaction(token, id);
      showToast('🗑️ Gelöscht', 'success');
      await refreshData();
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // Edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      description: item.description || '',
      amount: item.amount?.toString() || '',
      type: item.type || 'expense',
      category: item.category || '',
      frequency: item.frequency || 'monthly',
      day_of_month: item.day_of_month || 1,
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      type: 'expense',
      category: '',
      frequency: 'monthly',
      day_of_month: 1,
      is_active: true
    });
  };

  // Open New Modal
  const openNewModal = (type = 'expense') => {
    setEditingItem(null);
    resetForm();
    setFormData(prev => ({ ...prev, type }));
    setShowModal(true);
  };

  // Helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount || 0));
  };

  const getFrequencyText = (item) => {
    if (item.frequency === 'yearly') {
      return `Fällig: ${item.month_name || 'Jährlich'}`;
    }
    if (item.frequency === 'quarterly') {
      return 'Fällig: Quartalsweise';
    }
    return `Am ${item.day_of_month || 1}. jeden Monats`;
  };

  const getCategoryIcon = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName);
    return cat?.icon || '📋';
  };

  // Filter by type and frequency
  const monthlyExpenses = recurring.filter(r => r.type === 'expense' && r.frequency === 'monthly');
  const monthlyIncome = recurring.filter(r => r.type === 'income' && r.frequency === 'monthly');
  const yearlyPayments = recurring.filter(r => r.frequency === 'yearly' || r.frequency === 'quarterly');

  // Calculate Summary
  const totalMonthlyIncome = monthlyIncome
    .filter(r => r.is_active)
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    
  const totalMonthlyExpenses = monthlyExpenses
    .filter(r => r.is_active)
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    
  const fixedDifference = totalMonthlyIncome - totalMonthlyExpenses;

  // Render Guards
  if (!active) return null;

  if (!isAuthenticated) {
    return (
      <div className={`recurring-container ${isDark ? 'dark' : ''}`}>
        <div className="recurring-auth-required">
          <span>🔒</span>
          <p>Bitte einloggen</p>
        </div>
      </div>
    );
  }

  // ✅ v3.0: Warte auf Token aus Context
  if (loading || tokenLoading) {
    return (
      <div className={`recurring-container ${isDark ? 'dark' : ''}`}>
        <Loading icon="🔄" text="Lade Daueraufträge..." />
      </div>
    );
  }

  return (
    <div className={`recurring-container ${isDark ? 'dark' : ''}`}>
      
      {/* Header */}
      <header className="recurring-header">
        <div className="header-left">
          <div className="header-icon">🔄</div>
          <div>
            <h1>Daueraufträge</h1>
            <p>{recurring.length} Einträge • Fixe Differenz: <span className={fixedDifference >= 0 ? 'positive' : 'negative'}>{fixedDifference >= 0 ? '+' : ''}{formatCurrency(fixedDifference)} €</span></p>
          </div>
        </div>
      </header>

      {/* Main Grid - Expenses & Income */}
      <div className="recurring-grid">
        
        {/* Monatliche Ausgaben */}
        <div className="recurring-card">
          <div className="card-header">
            <h2><span>📅</span> Monatliche Ausgaben</h2>
          </div>
          <div className="recurring-list">
            {monthlyExpenses.length === 0 ? (
              <div className="empty-state">
                <p>Keine monatlichen Ausgaben</p>
              </div>
            ) : (
              monthlyExpenses.map(item => (
                <div 
                  key={item.id} 
                  className={`recurring-item ${!item.is_active ? 'inactive' : ''}`}
                  onClick={() => handleEdit(item)}
                >
                  <div className="item-icon">
                    {getCategoryIcon(item.category)}
                  </div>
                  <div className="item-info">
                    <div className="item-name">{item.description}</div>
                    <div className="item-schedule">{getFrequencyText(item)}</div>
                  </div>
                  <div className="item-amount expense">
                    -{formatCurrency(item.amount)} €
                  </div>
                  <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={item.is_active}
                      onChange={() => handleToggle(item)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Monatliche Einnahmen */}
        <div className="recurring-card">
          <div className="card-header">
            <h2><span>📅</span> Monatliche Einnahmen</h2>
          </div>
          <div className="recurring-list">
            {monthlyIncome.length === 0 ? (
              <div className="empty-state">
                <p>Keine monatlichen Einnahmen</p>
              </div>
            ) : (
              monthlyIncome.map(item => (
                <div 
                  key={item.id} 
                  className={`recurring-item ${!item.is_active ? 'inactive' : ''}`}
                  onClick={() => handleEdit(item)}
                >
                  <div className="item-icon">
                    {getCategoryIcon(item.category)}
                  </div>
                  <div className="item-info">
                    <div className="item-name">{item.description}</div>
                    <div className="item-schedule">{getFrequencyText(item)}</div>
                  </div>
                  <div className="item-amount income">
                    +{formatCurrency(item.amount)} €
                  </div>
                  <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={item.is_active}
                      onChange={() => handleToggle(item)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))
            )}
          </div>

          {/* Summary Box */}
          <div className="summary-box">
            <div className="summary-row">
              <span>Monatliche Einnahmen</span>
              <span className="income">+{formatCurrency(totalMonthlyIncome)} €</span>
            </div>
            <div className="summary-row">
              <span>Monatliche Ausgaben</span>
              <span className="expense">-{formatCurrency(totalMonthlyExpenses)} €</span>
            </div>
            <div className="summary-row total">
              <span>Fixe Differenz</span>
              <span className={fixedDifference >= 0 ? 'income' : 'expense'}>
                {fixedDifference >= 0 ? '+' : ''}{formatCurrency(fixedDifference)} €
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Jährliche Zahlungen */}
      {yearlyPayments.length > 0 && (
        <div className="recurring-card yearly">
          <div className="card-header">
            <h2><span>📆</span> Jährliche Zahlungen</h2>
          </div>
          <div className="yearly-grid">
            {yearlyPayments.map(item => (
              <div 
                key={item.id} 
                className={`yearly-item ${!item.is_active ? 'inactive' : ''}`}
                onClick={() => handleEdit(item)}
              >
                <div className="item-icon">
                  {getCategoryIcon(item.category)}
                </div>
                <div className="item-info">
                  <div className="item-name">{item.description}</div>
                  <div className="item-schedule">{getFrequencyText(item)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? '✏️ Dauerauftrag bearbeiten' : '➕ Neuer Dauerauftrag'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              
              {/* Type Toggle */}
              <div className="type-toggle">
                <button 
                  className={formData.type === 'expense' ? 'active expense' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, type: 'expense' }))}
                >
                  💸 Ausgabe
                </button>
                <button 
                  className={formData.type === 'income' ? 'active income' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, type: 'income' }))}
                >
                  💰 Einnahme
                </button>
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Bezeichnung *</label>
                <input 
                  type="text"
                  placeholder="z.B. Miete, Netflix, Gehalt..."
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              {/* Amount */}
              <div className="form-group">
                <label>Betrag *</label>
                <div className="input-with-suffix">
                  <input 
                    type="number"
                    placeholder="0,00"
                    step="0.01"
                    value={formData.amount}
                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  />
                  <span className="suffix">€</span>
                </div>
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Kategorie</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="">-- Keine --</option>
                  {categories
                    .filter(c => c.type === formData.type)
                    .map(cat => (
                      <option key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Frequency */}
              <div className="form-group">
                <label>Häufigkeit</label>
                <select
                  value={formData.frequency}
                  onChange={e => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                >
                  <option value="monthly">Monatlich</option>
                  <option value="quarterly">Quartalsweise</option>
                  <option value="yearly">Jährlich</option>
                </select>
              </div>

              {/* Day of Month (for monthly) */}
              {formData.frequency === 'monthly' && (
                <div className="form-group">
                  <label>Tag im Monat</label>
                  <select
                    value={formData.day_of_month}
                    onChange={e => setFormData(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                  >
                    {[...Array(28)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}.</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Active Toggle */}
              <div className="form-group toggle-group">
                <label>Aktiv</label>
                <label className="toggle-switch">
                  <input 
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

            </div>
            <div className="modal-footer">
              {editingItem && (
                <button 
                  className="btn-delete"
                  onClick={() => {
                    handleDelete(editingItem.id);
                    setShowModal(false);
                  }}
                >
                  🗑️ Löschen
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handleSave}>
                💾 Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

export default RecurringTransactions;

